import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { AtsJobApplicationFilesOfferLetterDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SendOfferLetterModal } from 'modules/ats/components/SendOfferLetterModal'
import { OfferLetterFromScratchModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromScratch'
import { OfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromTemplate'
import { SenfOfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/SenfOfferLetterFromTemplate'

import { OfferLetterItem } from './OfferLetterItem'
import { UnsendOfferLetterItem } from './UnsendOfferLetterItem'

interface Props {
  applicationId: string
  jobPostingId: string
}

export const OfferLetter = ({ applicationId, jobPostingId }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [openedModal, setOpenedModal] = useState<
    | 'send-offer-letter'
    | 'create-offer-letter-from-template'
    | 'send-offer-letter-from-scratch'
    | 'send-offer-letter-from-template'
    | undefined
  >(undefined)
  const { data, refetch } = useQuery(AtsJobApplicationFilesOfferLetterDocument, {
    variables: { applicationId, companyId: company.id, jobPostingId },
  })

  const devOfferLetterTemplatesEnabled =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_offer_letter_templates'
    )?.enabled ?? false

  const isHiringPlanEnabled =
    data?.ats._policies.sidebarSeeRecruitmentHiringPlanSubLevelItem.can ?? false

  const offerLetterData = data?.ats.offerLetters[0]
  const unsendOfferLetter = data?.ats.unsendOfferLetter?.nodes?.[0]
  const applicationData = data?.ats.application
  const hasVacancy = !!applicationData?.vacancy?.id

  const handleSaveForLater = () => {
    setOpenedModal(undefined)
    refetch()
    app.setFlash({
      type: 'correct',
      message: i18n.t('ats.send_offer_letter_modal.from_template.save_for_later_success'),
    })
  }

  if (!applicationData) {
    return null
  }

  const hasOfferLetter = !!offerLetterData
  const hasUnsendOfferLetter = !!unsendOfferLetter

  return (
    <>
      {hasOfferLetter ? (
        <OfferLetterItem
          offerLetter={offerLetterData}
          applicationId={applicationId}
          jobPostingId={jobPostingId}
        />
      ) : hasUnsendOfferLetter ? (
        <UnsendOfferLetterItem
          unsendOfferLetter={unsendOfferLetter}
          applicationId={applicationId}
          companyId={company.id}
          jobPostingId={jobPostingId}
          onSend={() => setOpenedModal('send-offer-letter-from-template')}
        />
      ) : (
        <OneEmptyState
          title={i18n.t('ats.candidates.files.offer_letter.empty_state.title')}
          description={i18n.t('ats.candidates.files.offer_letter.empty_state.description')}
          emoji="📝"
          actions={[
            {
              label: i18n.t(
                'ats.candidates.files.offer_letter.empty_state.actions.send_offer_letter'
              ),
              onClick: () => setOpenedModal('send-offer-letter'),
            },
            {
              label: i18n.t('ats.candidates.files.offer_letter.empty_state.actions.templates'),
              onClick: () => navigate(navigation.settings.ats.templates.fullPath()),
              variant: 'outline',
            },
          ]}
        />
      )}

      {openedModal === 'send-offer-letter' &&
        // TO DO: show only SendOfferLetterModal when feature is ready
        (devOfferLetterTemplatesEnabled ? (
          <SendOfferLetterModal
            onSelectOption={(option) => {
              setOpenedModal(option)
            }}
            onClose={() => setOpenedModal(undefined)}
            applicationId={applicationId}
            jobPostingId={jobPostingId}
            isHiringPlanEnabled={isHiringPlanEnabled}
            hasVacancy={hasVacancy}
          />
        ) : (
          <OfferLetterFromScratchModal
            application={applicationData}
            onClose={() => setOpenedModal(undefined)}
          />
        ))}

      {openedModal === 'send-offer-letter-from-scratch' && (
        <OfferLetterFromScratchModal
          application={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'create-offer-letter-from-template' && (
        <OfferLetterFromTemplateModal
          jobApplicationId={applicationId}
          onClose={() => setOpenedModal(undefined)}
          onSuccess={() => setOpenedModal('send-offer-letter-from-template')}
          onSaveForLater={handleSaveForLater}
        />
      )}

      {openedModal === 'send-offer-letter-from-template' && (
        <SenfOfferLetterFromTemplateModal
          applicationData={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
    </>
  )
}
